import { useEffect } from 'react';
import { useFetch } from "modules/main/hooks/useFetch";
import {FetchDataOptions} from 'modules/main/services/fetchData';
import { UserData } from 'modules/authentication/types/user-data';
import {useLocation, useNavigate, useParams } from "react-router-dom";
import { FilterBlock } from "../components/filterBlock";
import { SearchResults } from "../components/searchResults";
import { SearchBar } from "modules/main/components/searchBar";
import { usePageTitle } from "hooks/usePageTitle";
import { PageBreadcrumbs } from "modules/main/components/page-breadcrumbs";
import { useMediaQuery } from "@/hooks/use-media-query";
import { isDesktopSize } from "utils/is-desktop";

import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowDownNarrowWide, ArrowDownWideNarrow, SearchCheck } from 'lucide-react';

import { CatalogResponse } from 'modules/catalog/types/catalogResponse';
import { endPointCatalogSearch} from "modules/main/services/endpoints";
import { filterParam } from "modules/catalog/types/filters_params";
import { getVisitorId } from 'utils/analytics';

export const Search = () => {
    const navigate = useNavigate();
    const { setTitle, setBreadcrumb, setShowBack } = usePageTitle();

    const location = useLocation();
    const isDesktop = useMediaQuery(isDesktopSize);
    const path_params = useParams();    
    const params = new URLSearchParams(location.search);
    const currentPage = params.get("page") ? params.get("page") : "";

    const allowedSorts = ["relevance", "lower_price", "higher_price"];
    const rawSortBy = params.get("sortBy");
    const sortBy = allowedSorts.includes(rawSortBy ?? "") ? rawSortBy! : "relevance";

    
    const brand_filters:filterParam = {
        name:"brand", 
        value:params.getAll("brand") ? params.getAll("brand") : []
    }

    const tag_filters:filterParam = {
        name:"tag",
        value:params.getAll("tag") ? params.getAll("tag") : []
    }    

    const section  = path_params.q ?? "";

    const requestOptions:FetchDataOptions = {
        method:"GET",
        headers:{
            "x-visitor-id": getVisitorId()
        },
        body:undefined
    }
    
    const userData = JSON.parse(localStorage.getItem("userData")??"{}") as UserData;
    const {data,error,loading} = useFetch<CatalogResponse>(endPointCatalogSearch(section,currentPage,sortBy,[brand_filters, tag_filters]),requestOptions);


    useEffect(() => {
        setTitle(section ? `Búsqueda: ${section}` : "Búsqueda");
        // EXTENSION 8: defensively reset the global back arrow so a stale
        // ArrowLeft from a previous route never shows on search results.
        setShowBack(false);
        window.scrollTo({ top: 0 });
    }, [currentPage,section,currentPage,sortBy,userData.location_id,[brand_filters, tag_filters], setTitle, setShowBack, section]);  

    // EXTENSION 8 (R2): /search/:q declares Inicio > "Resultados búsqueda".
    // Dedicated effect with query-independent deps — the effect above re-runs
    // on every render (inline [brand_filters, tag_filters] array literals), so
    // setting the breadcrumb there would loop. The crumb label is deliberately
    // query-free (D3), so pagination/sort/filter changes never re-declare it.
    useEffect(() => {
        setBreadcrumb(
            <PageBreadcrumbs
                crumbs={[
                    { label: "Inicio", to: "/store" },
                    { label: "Resultados búsqueda" },
                ]}
            />,
        );
        return () => setBreadcrumb(null);
    }, [setBreadcrumb]);

    if(data && data.attribution_token){
        window.dataLayer.push({
        'event': 'view_search_results', 
        'search_query': section,        
        'attribution_token': data.attribution_token, 
        'visitor_id': getVisitorId() 
        });
    }

    return(
        <>
        {/* {(error)&&(<div>Error: {error}</div>)} */}
        {/* {(!loading && !error && data) && */}
        {
        <main className="py-2 lg:py-4 px-4 lg:px-16 flex flex-col gap-2 lg:gap-4">
            <SearchBar key={section} defaultValue={section} className="mx-auto w-full lg:w-[60dvw] mb-2" />
            {
            (!isDesktop)?(
            <div className="flex flex-row justify-between">
                <div className="flex gap-2 items-center">
                    <FilterBlock 
                        isSearch={true} 
                        isDesktop={false} 
                        section={section} 
                        initialFilters={[brand_filters,tag_filters]}
                        data={data?.filters??[]}
                        />
                    <span className="font-bold text-xs">Filtrar</span>        
                </div>
                <div className='flex gap-2 py-2 items-center'> 

                    <Select value={sortBy} 
                    onValueChange={
                        (value)=>{
                            const currentParams = new URLSearchParams(location.search);
                            currentParams.set('sortBy', value);
                            currentParams.delete('page'); // Reiniciar paginación
                            currentParams.delete('prevpage');
                            navigate(`${location.pathname}?${currentParams.toString()}`);
                            // setSortBy(value);
                        }}>
                        <SelectTrigger className='text-muted-foreground w-16' >
                            <SelectValue placeholder="Ordenar Por">
                                {
                                (sortBy === "relevance") ?  <SearchCheck className="w-4 h-4"/> :
                                (sortBy === "lower_price") ? <ArrowDownNarrowWide className="w-4 h-4"/> :
                                <ArrowDownWideNarrow className="w-4 h-4"/>
                                }                        
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent id="sortby">
                            <SelectItem value='relevance'>
                                <div className='flex gap-2'><SearchCheck className="w-4 h-4"/>Por Relevancia</div>
                            </SelectItem>                          
                            <SelectItem value='lower_price'>
                                <div className='flex gap-2'><ArrowDownNarrowWide className="w-4 h-4"/>Menor Precio</div>
                            </SelectItem>
                            <SelectItem value='higher_price'>
                                <div className='flex gap-2'><ArrowDownWideNarrow className="w-4 h-4"/>Mayor Precio</div>
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div> 
            ):(
            <>                
            </>       
            )
            }             
            <div className="flex flex-row gap-4">
                {
                (isDesktop)&&(
                <div className="w-80 px-2" >                
                    <FilterBlock isSearch={true} isDesktop={true} section={section} initialFilters={[brand_filters,tag_filters]}
                        data={data?.filters??[]}/>
                </div>
                )
                }
                
                <div className='flex flex-1 flex-col w-full gap-2'>
                    {(isDesktop)&&               
                    (
                    <div className='flex w-full justify-between align-middle'> 
                        <h3 className="py-2 text-sm"><span className="text-muted-foreground font-bold">RESULTADOS DE BÚSQUEDA</span></h3>
                        <div className='flex gap-2 py-2 items-center'> 
                            <Label className='text-xs text-muted-foreground' htmlFor="sortby">Orden</Label>
                            <Select value={sortBy} onValueChange={(value)=>
                                {
                                    const currentParams = new URLSearchParams(location.search);
                                    currentParams.set('sortBy', value);
                                    currentParams.delete('page'); // Reiniciar paginación
                                    currentParams.delete('prevpage');
                                    navigate(`${location.pathname}?${currentParams.toString()}`);
                                    // setSortBy(value)
                                    }}>
                                <SelectTrigger className='w-[18em] text-muted-foreground'>
                                    <SelectValue placeholder="Ordenar Por"/>
                                </SelectTrigger>
                                <SelectContent id="sortby">
                                    <SelectItem value='relevance'>
                                        <div className='flex gap-2'><SearchCheck className="w-4 h-4"/>Por Relevancia</div>
                                    </SelectItem>                          
                                    <SelectItem value='lower_price'>
                                        <div className='flex gap-2'><ArrowDownNarrowWide className="w-4 h-4"/>Menor Precio</div>
                                    </SelectItem>
                                    <SelectItem value='higher_price'>
                                        <div className='flex gap-2'><ArrowDownWideNarrow className="w-4 h-4"/>Mayor Precio</div>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>    
                    </div>
                    )
                    }                
                    <SearchResults 
                        section={section}
                        brand_filters={brand_filters}
                        tag_filters={tag_filters}
                        currentPage={currentPage}
                        search_params={location.search}
                        isMobile={!isDesktop}  
                        data={data ?? {} as CatalogResponse}
                        error={error}
                        loading={loading}
                        attribution_token={data?.attribution_token}
                    />    
                </div>         
            </div>
        </main>}
        </>
        );
}
